import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Alert,
  Table,
} from "react-bootstrap";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { getInformeEstaciones } from "../helpers/rutaReportes";

const fmtARS = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const todayYYYYMMDD = () => new Date().toISOString().slice(0, 10);

export default function InformeEstaciones() {
  const [fecha, setFecha] = useState(todayYYYYMMDD());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const consultar = async () => {
    setErr("");
    setLoading(true);

    try {
      const r = await getInformeEstaciones(fecha);
      if (!r?.ok) {
        throw new Error(r?.error || r?.msg || "No se pudo cargar el reporte");
      }
      setData(r);
    } catch (e) {
      setData(null);
      setErr(e?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  const exportarPDF = async () => {
    if (!data) return;

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

    const tot = data?.totales || {};
    const estaciones = data?.estaciones || [];

    doc.setFontSize(16);
    doc.text("Resumen de Saldos - Estaciones", 14, 14);

    doc.setFontSize(10);
    doc.text(`Fecha de corte: ${data?.fechaCorte || fecha}`, 14, 22);
    doc.text(`Total deuda: $ ${fmtARS(tot.debe)}`, 14, 30);
    doc.text(`Total saldo a favor: $ ${fmtARS(tot.afavor)}`, 14, 36);
    doc.text(`Neto: $ ${fmtARS(tot.neto)}`, 14, 42);

    autoTable(doc, {
      startY: 48,
      head: [["Estación", "Debe", "Saldo a favor", "Saldo final"]],
      body: estaciones.map((x) => [
        x?.estacion || "",
        x?.debe ? `$ ${fmtARS(x.debe)}` : "",
        x?.afavor ? `$ ${fmtARS(x.afavor)}` : "",
        `$ ${fmtARS(x.saldo)}`,
      ]),
      styles: { fontSize: 8, cellPadding: 1.4 },
      headStyles: { fontStyle: "bold" },
      columnStyles: {
        1: { halign: "right", cellWidth: 35 },
        2: { halign: "right", cellWidth: 35 },
        3: { halign: "right", cellWidth: 35 },
      },
      didParseCell: (hook) => {
        if (hook.section !== "body") return;
        if (hook.column.index === 3) {
          const row = estaciones[hook.row.index];
          if (Number(row?.saldo || 0) < 0) {
            hook.cell.styles.textColor = [13, 110, 253];
            hook.cell.styles.fontStyle = "bold";
          } else {
            hook.cell.styles.textColor = [25, 135, 84];
            hook.cell.styles.fontStyle = "bold";
          }
        }
      },
    });

    doc.save(`informe-estaciones-${data?.fechaCorte || fecha}.pdf`);
  };

  const tot = data?.totales || {};

  const estaciones = useMemo(() => {
    return [...(data?.estaciones || [])].sort((a, b) =>
      String(a?.estacion || "").localeCompare(String(b?.estacion || ""), "es", {
        sensitivity: "base",
      })
    );
  }, [data]);

  return (
    <Container fluid="md" className="py-3">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-3">
            <div style={{ minWidth: 240 }}>
              <Form.Group>
                <Form.Label className="mb-1">Fecha de corte</Form.Label>
                <Form.Control
                  type="date"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                />
              </Form.Group>
            </div>

            <div className="d-flex gap-2">
              <Button onClick={consultar} disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size="sm" /> Consultando...
                  </>
                ) : (
                  "Consultar"
                )}
              </Button>

              <Button
                variant="secondary"
                onClick={exportarPDF}
                disabled={!data || loading}
              >
                Exportar PDF
              </Button>
            </div>
          </div>

          {err ? (
            <Alert className="mt-3 mb-0" variant="danger">
              {err}
            </Alert>
          ) : null}
        </Card.Body>
      </Card>

      <div className="mt-3">
        {!data ? (
          <div className="text-muted">
            Elegí una fecha y tocá <strong>Consultar</strong>.
          </div>
        ) : (
          <div>
            <Row className="g-2">
            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Total deuda estaciones
                  </div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: 24, lineHeight: 1.1, color: "#198754" }}
                  >
                    $ {fmtARS(tot.debe)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Total saldo a favor
                  </div>
                  <div
                    className="fw-bold"
                    style={{ fontSize: 24, lineHeight: 1.1, color: "#0d6efd" }}
                  >
                    $ {fmtARS(tot.afavor)}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={4}>
              <Card className="border-0 shadow-sm">
                <Card.Body className="py-3">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Neto general
                  </div>
                  <div
                    className="fw-bold"
                    style={{
                      fontSize: 24,
                      lineHeight: 1.1,
                      color: Number(tot.neto || 0) < 0 ? "#0d6efd" : "#198754",
                    }}
                  >
                    $ {fmtARS(tot.neto)}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="mt-3 shadow-sm border-0">
            <Card.Body>
              <h5 className="mb-3">Estaciones</h5>

              <div className="table-responsive">
                <Table
                  striped
                  bordered
                  hover
                  size="sm"
                  className="mb-0"
                  style={{ tableLayout: "fixed" }}
                >
                  <thead>
                    <tr>
                      <th>Estación</th>
                      <th style={{ width: 160, textAlign: "right" }}>Debe</th>
                      <th style={{ width: 160, textAlign: "right" }}>Saldo a favor</th>
                      <th style={{ width: 160, textAlign: "right" }}>Saldo final</th>
                    </tr>
                  </thead>
                  <tbody>
                    {estaciones.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center">
                          Sin saldos.
                        </td>
                      </tr>
                    ) : (
                      estaciones.map((e) => (
                        <tr key={e._id || e.estacion}>
                          <td>{e.estacion}</td>
                          <td style={{ textAlign: "right" }}>
                            {e.debe ? `$ ${fmtARS(e.debe)}` : ""}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {e.afavor ? `$ ${fmtARS(e.afavor)}` : ""}
                          </td>
                          <td
                            style={{
                              textAlign: "right",
                              fontWeight: "bold",
                              color: Number(e.saldo || 0) < 0 ? "#0d6efd" : "#198754",
                            }}
                          >
                            $ {fmtARS(e.saldo)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
          </div>
        )}
      </div>
    </Container>
  );
}